import { OidcClient } from '@axa-fr/react-oidc';
import { getLayoutView as getLayoutViewFromLib } from '@healthhub/message-channel';
import type { Location } from 'react-router';
import { matchRoutes } from 'react-router';

import { LayoutType, SourceApp, SourceAppView } from '@shared/constants/constants';
import type { SourceAppViewType } from '@shared/constants/constants';
import type { RouteConfig } from '@shared/types/common.types';
import { flattenRoute } from '@shared/utils/routes';

import type { LayoutViewContextValue } from './LayoutViewContext';

interface GetLayoutViewValueParams {
  location: Partial<Location> | string;
  ROUTES: RouteConfig[];
}

const getOidcTokens = () => {
  try {
    return OidcClient.get().tokens;
  } catch {
    // OIDC is not initialised yet — `getLayoutViewFromLib` still resolves the source app from the user agent.
    return undefined;
  }
};

/**
 * Resolves the layout view for a location. Free of React and of the router's context, so it can run
 * both inside `LayoutViewContextProvider` and before any of it has mounted.
 */
export const getLayoutViewValue = ({ location, ROUTES }: GetLayoutViewValueParams): LayoutViewContextValue => {
  const flattenedRoutes = flattenRoute(ROUTES);
  const matchedPath = (matchRoutes(flattenedRoutes, location)?.[0]?.route ?? {}) as RouteConfig;
  const { availableViews = [] } = matchedPath;

  const {
    isMobileView: isMobileViewFromLib,
    sourceApp,
    capabilityVersion,
  } = getLayoutViewFromLib({ token: getOidcTokens() });

  const isStandardView = !!(sourceApp && sourceApp !== SourceApp.HH && availableViews.includes(LayoutType.STANDARD));
  const isSharedServiceView = !!(sourceApp && sourceApp !== SourceApp.HH && availableViews.includes(LayoutType.SHARED));
  const isCommonView = availableViews.includes(LayoutType.COMMON);
  const isMobileView = isMobileViewFromLib && !isCommonView && availableViews.includes(LayoutType.MOBILE);
  const isPortalView = !isStandardView && !isMobileView && !isSharedServiceView && !isCommonView;

  const source = sourceApp || SourceApp.HH;
  let sourceAppView: SourceAppViewType;
  if (source === SourceApp.HH) {
    sourceAppView = isPortalView ? SourceAppView.HH_WEB : SourceAppView.HH_MOBILE;
  } else {
    sourceAppView = SourceAppView[sourceApp as keyof typeof SourceAppView];
  }

  return {
    capabilityVersion,
    isCommonView,
    isMobileView,
    isPortalView,
    isSharedServiceView,
    isStandardView,
    sourceApp: source,
    sourceAppView,
  };
};
